import { useState } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PDFThumbnail } from "./PDFThumbnail";
import { CreateInvoiceForm } from "./CreateInvoiceForm";
import { DeleteConfirmationDialog } from "./DeleteConfirmationDialog";
import { Download, Trash2, Calendar, Pencil } from "lucide-react";
import { showToast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import { enUS, pt } from "date-fns/locale";
import { useTranslation } from "react-i18next"; // If you're using i18next


interface InvoiceCardProps {
  invoice: any;
  onSelect?: (id: string) => void;
  isSelected?: boolean;
  onCheckboxChange?: (checked: boolean, id: string) => void;
}

export const InvoiceCard = ({
  invoice,
  onSelect,
  isSelected,
  onCheckboxChange
}: InvoiceCardProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const queryClient = useQueryClient();

  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pt" ? pt : enUS;

  // Calculate total amount
  const totalAmount = (invoice.subtotal || 0) + (invoice.total_tax_amount || 0);

  const handleDownload = async () => {
    try {
      const filePath = invoice.file_path.includes("public/invoices/")
        ? invoice.file_path.split("public/invoices/")[1]
        : invoice.file_path;

      const { data, error } = await supabase.storage
        .from("invoices")
        .download(filePath);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast.success(t("toasts.invoice_downloaded_successfully", "Invoice downloaded successfully"));
    } catch (error) {
      console.error("Download error:", error);
      showToast.error(t("toasts.invoice_downloaded_failed", "Failed to download invoice"));
    }
  };

  const handleDelete = async () => {
    try {
      const { data: schedules, error: scheduleQueryError } = await supabase
        .from('invoice_schedules')
        .select('id')
        .eq('invoice_id', invoice.id);

      if (scheduleQueryError) {
        console.error('Error querying schedules:', scheduleQueryError);
        showToast.error(t("toasts.delete_invoice_failed", "Failed to delete invoice"));
        setShowDeleteDialog(false);
        return;
      }

      if (schedules && schedules.length > 0) {
        const scheduleIds = schedules.map(s => s.id);

        const { error: remindersError } = await supabase
          .from('invoice_schedule_reminders')
          .delete()
          .in('invoice_schedule_id', scheduleIds);

        if (remindersError) {
          console.error('Error deleting reminders:', remindersError);
          showToast.error(t("toasts.delete_invoice_failed", "Failed to delete invoice"));
          setShowDeleteDialog(false);
          return;
        }

        const { error: schedulesError } = await supabase
          .from('invoice_schedules')
          .delete()
          .eq('invoice_id', invoice.id);

        if (schedulesError) {
          console.error('Error deleting schedules:', schedulesError);
          showToast.error(t("toasts.delete_invoice_failed", "Failed to delete invoice"));
          setShowDeleteDialog(false);
          return;
        }
      }

      const { error: invoiceError } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoice.id);

      if (invoiceError) {
        console.error('Error deleting invoice:', invoiceError);
        showToast.error(t("toasts.delete_invoice_failed", "Failed to delete invoice"));
        setShowDeleteDialog(false);
        return;
      }

      setShowDeleteDialog(false);
      await queryClient.invalidateQueries({ queryKey: ["invoices-with-schedules"] });
      showToast.success(t("toasts.delete_invoice_success", "Invoice deleted successfully"));
    } catch (error) {
      console.error('Error deleting invoice:', error);
      showToast.error(t("toasts.delete_invoice_failed", "Failed to delete invoice"));
      setShowDeleteDialog(false);
    }
  };

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ["invoices-with-schedules"] });
    setIsEditOpen(false);
    setIsScheduleOpen(false);
  };

  return (
    <>
      <Card
        className={`group relative cursor-pointer bg-[#FFFFFF] hover:shadow-[6px_6px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200 ${isSelected ? "ring-2 ring-[#8791F3]" : "ring-1 ring-[#636782]"
          }`}
        onClick={() => onSelect?.(invoice.id)}
      >
        {invoice.status === "paid" && (
          <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden z-10">
            <div className="bg-[#5CC2BB] text-[#34375B] text-xs font-bold py-1 w-32 text-center rotate-45 origin-bottom-left translate-x-[20px] translate-y-[-38px] shadow-md">
              {t("dashboard.paid", "Paid")}
            </div>
          </div>
        )}
        <CardContent className="p-4 bg-[#FFFFFF] rounded-lg">
          {/* <PDFThumbnail filePath={invoice.file_path} /> */}
          <div className="mt-4 space-y-2">
            <h1 className="font-semibold text-[#8791f3]">{invoice.invoice_number}</h1>
            <p className="text-sm text-[#636782]">
              {new Date(invoice.issue_date).toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            <p className="text-sm">{invoice.recipient_name}</p>
            <p className="text-sm text-[#636782]">
              {new Intl.NumberFormat(i18n.language, { style: 'currency', currency: invoice.currency || 'EUR' }).format(totalAmount)}
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between p-4">
          <div className="flex items-center space-x-2 md:opacity-0 opacity-100 md:group-hover:opacity-100 transition-opacity">
            <Button
              className="text-[#636782] hover:text-[#34375B] hover:bg-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
              variant="outline"
              size="icon"
              onClick={e => {
                e.stopPropagation();
                handleDownload();
              }}
              title="Download PDF"
            >
              <Download className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={e => {
                e.stopPropagation();
                setShowDeleteDialog(true);
              }}
              className="text-destructive text-[#F39D96] hover:text-[#F44336] hover:bg-transparent hover:shadow-[4px_4px_0px_0px_#F44336] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
              title="Delete Invoice"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
            <Button
              className="text-[#636782] hover:text-[#34375B] hover:bg-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
              variant="outline"
              size="icon"
              onClick={e => {
                e.stopPropagation();
                setIsEditOpen(true);
              }}
              title="Edit Invoice"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              className="text-[#636782] hover:text-[#34375B] hover:bg-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
              variant="outline"
              size="icon"
              onClick={e => {
                e.stopPropagation();
                setIsScheduleOpen(true);
              }}
              title="Schedule Invoice"
            >
              <Calendar className="h-3 w-3" />
            </Button>
          </div>
        </CardFooter>
      </Card>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-6 bg-[#F1F6F5] border-none">
          <DialogTitle className="text-lg font-semibold mb-4">
            Edit Invoice
          </DialogTitle>
          <CreateInvoiceForm
            mode="edit"
            existingInvoice={invoice}
            onSuccess={handleSuccess}
            onCancel={() => setIsEditOpen(false)}
            initialDetailsExpanded={true}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isScheduleOpen} onOpenChange={setIsScheduleOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-6 bg-[#F1F6F5] border-none">
          <DialogTitle className="text-lg font-semibold mb-4">
            Schedule Invoice
          </DialogTitle>
          <CreateInvoiceForm
            mode="edit"
            existingInvoice={invoice}
            onSuccess={handleSuccess}
            onCancel={() => setIsScheduleOpen(false)}
            initialScheduleExpanded={true}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        selectedCount={1}
      />
    </>
  );
};
